import type { ReactNode } from 'react'
import styled from '@emotion/styled'
import { getContrastRatio, useTheme } from '@mui/system'

import { LoadingSpinner } from 'src/system/ui/LoadingSpinner'
import {
  brighten,
  dim,
  hoverAmount,
  hoverTransition
} from 'src/system/ui/theme/hover'

export enum ButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  accent = 'accent',
  danger = 'danger',
  warning = 'warning',
  text = 'text',
  link = 'link'
}

export interface ButtonProps {
  children: ReactNode
  className?: string
  onClick?: () => void
  type?: 'button' | 'submit'
  fullWidth?: boolean
  variant?: ButtonVariant
  disabled?: boolean
  cursor?: string
  pointerEvents?: string
  color?: string
  // Darken the fill on hover instead of brightening it. For brand colors
  // (Discord's blurple) whose white label would drop below 4.5:1 otherwise.
  darkenOnHover?: boolean
  'aria-label'?: string
  processing?: boolean
}

type OptionalProps = Pick<ButtonProps, 'disabled' | 'color'>

type StyleProps = {
  width?: string
  background?: string
  border?: string
  padding: string
  fontFamily: string
  underline: boolean
  hoverBackground: string
  hoverColor: string
  halo: boolean
}

export function Button(props: ButtonProps) {
  const width = props.fullWidth ? '100%' : 'auto'

  const defaultVariant = ButtonVariant.primary
  const variant = props.variant ?? defaultVariant

  const background = useBackground(variant, {
    disabled: props.disabled,
    color: props.color
  })
  const border = useBorder(variant)
  const cursor = props.disabled ? 'default' : 'pointer'
  const underline = props.variant === ButtonVariant.link
  const pointerEvents = props.disabled || props.processing ? 'none' : 'all'
  const color = useColor(variant, background, {
    disabled: props.disabled
  })
  const padding = usePadding(variant)
  const fontFamily = useFontFamily(variant)

  // Filled buttons brighten their background (plus a soft halo); text/link
  // variants have no fill, so they brighten their text like a link instead.
  const isFilled = background !== 'transparent'
  const hoverFill = props.darkenOnHover ? dim : brighten
  const hoverBackground =
    props.disabled || !isFilled
      ? background
      : hoverFill(background, hoverAmount.fill)
  const hoverColor =
    props.disabled || isFilled ? color : brighten(color, hoverAmount.text)
  const halo = isFilled && !props.disabled

  return (
    <StyledButton
      className={props.className}
      onClick={props.onClick}
      width={width}
      type={props.type ?? 'submit'}
      background={background}
      border={border}
      cursor={cursor}
      pointerEvents={pointerEvents}
      color={color}
      aria-label={props['aria-label']}
      disabled={!!props.disabled}
      padding={padding}
      fontFamily={fontFamily}
      underline={underline}
      hoverBackground={hoverBackground}
      hoverColor={hoverColor}
      halo={halo}
    >
      <Content processing={props.processing}>{props.children}</Content>
    </StyledButton>
  )
}

function Content(props: { children: ReactNode; processing?: boolean }) {
  if (props.processing) {
    return <LoadingSpinner />
  }

  return <>{props.children}</>
}

function useBackground(
  variant: ButtonVariant,
  { disabled, color }: OptionalProps
) {
  const theme = useTheme()

  if (variant === ButtonVariant.text || variant === ButtonVariant.link) {
    return 'transparent'
  }

  if (disabled) {
    return theme.d.colors.button.disabled.background
  }

  if (color) {
    return color
  }

  if (variant === ButtonVariant.accent) {
    return theme.d.colors.accent
  }

  if (variant === ButtonVariant.danger) {
    return theme.d.colors.button.danger
  }

  if (variant === ButtonVariant.secondary) {
    return theme.d.colors.button.secondary
  }

  return theme.d.colors.button.primaryFill
}

function useBorder(variant: ButtonVariant) {
  const theme = useTheme()

  if (variant === ButtonVariant.text) {
    return 'none'
  }

  if (variant === ButtonVariant.secondary) {
    return `1px solid ${theme.d.colors.border.light}`
  }

  return 'none'
}

function useColor(
  variant: ButtonVariant,
  background: string,
  { disabled }: OptionalProps
) {
  const theme = useTheme()
  if (disabled) {
    return theme.d.colors.button.disabled.text
  }

  if (variant === ButtonVariant.link) {
    return theme.d.colors.link.default
  }

  if (variant === ButtonVariant.text) {
    return theme.d.colors.button.primary
  }

  // Filled buttons take a white label, unless the fill is too light for white
  // to read 4.5:1 (the lifetime gold); those take the page's dark instead.
  const label = theme.d.colors.text.secondary
  return getContrastRatio(label, background) >= 4.5
    ? label
    : theme.d.colors.background
}

function usePadding(variant: ButtonVariant = ButtonVariant.primary) {
  const theme = useTheme()

  if (variant === ButtonVariant.link) {
    return '0'
  }

  if (variant === ButtonVariant.text) {
    return '4px'
  }

  return `${theme.d.spacing[3]} ${theme.d.spacing[7]}`
}

function useFontFamily(variant: ButtonVariant) {
  const theme = useTheme()

  if (variant === ButtonVariant.link) {
    return theme.d.font.primary
  }

  return theme.d.font.secondary
}

const StyledButton = styled.button<ButtonProps & StyleProps>`
  padding: ${(props) => props.padding};
  font-weight: normal;
  border-radius: 4px;
  background-color: ${(props) => props.background};
  font-family: ${(props) => props.fontFamily};
  border: ${(props) => props.border};
  width: ${(props) => props.width};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => props.cursor};
  color: ${(props) => props.color};
  pointer-events: ${(props) => props.pointerEvents};
  transition: background-color ${hoverTransition}, color ${hoverTransition},
    box-shadow ${hoverTransition};

  svg {
    margin-right: 6px;
  }

  &:hover {
    background-color: ${(props) => props.hoverBackground};
    color: ${(props) => props.hoverColor};
    text-decoration: ${(props) => (props.underline ? 'underline' : 'none')};
    ${(props) =>
      props.halo
        ? `box-shadow: 0 0 0 1px ${props.hoverBackground}, 0 6px 20px -8px ${props.background};`
        : ''}
  }
`
